import { useState } from "react";
import { toast } from "sonner";
import { Check, ArrowRight, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";

const STEPS = [
  { id: 1, label: "Personal" },
  { id: 2, label: "Business" },
  { id: 3, label: "Vehicle" },
  { id: 4, label: "Review" },
];

const initial = {
  full_name: "",
  email: "",
  phone: "",
  city: "",
  date_of_birth: "",
  drivers_license: "",
  abn: "",
  employment_status: "Self-employed",
  monthly_income: "",
  vehicle_interest: "",
  deposit_amount: "",
  term_years: "3",
  additional_notes: "",
};

const CITIES = [
  "Sydney", "Melbourne", "Brisbane", "Perth", "Adelaide",
  "Canberra", "Hobart", "Darwin", "Other",
];

export default function Apply() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [applicationId, setApplicationId] = useState("");

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validateStep = () => {
    if (step === 1) {
      if (!form.full_name || !form.email || !form.phone || !form.city || !form.date_of_birth) {
        toast.error("Please complete all personal details.");
        return false;
      }
    }
    if (step === 2) {
      if (!form.abn || !form.drivers_license || !form.monthly_income) {
        toast.error("Please complete business and licence details.");
        return false;
      }
      if (form.abn.replace(/\s/g, "").length !== 11) {
        toast.error("ABN must be 11 digits.");
        return false;
      }
    }
    if (step === 3) {
      if (!form.vehicle_interest || !form.deposit_amount || !form.term_years) {
        toast.error("Please complete vehicle preferences.");
        return false;
      }
      if (Number(form.deposit_amount) < 4000) {
        toast.error("Advance payment must be at least 13.5% of the vehicle price.");
        return false;
      }
    }
    return true;
  };

  const next = () => {
    if (validateStep()) setStep((s) => Math.min(4, s + 1));
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await api.post("/applications", form);
      setApplicationId(res.data.id);
      setSubmitted(true);
      toast.success("Application received. We'll be in touch soon.");
    } catch (err) {
      toast.error("Unable to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6 py-20" data-testid="apply-success">
        <div className="max-w-xl w-full text-center">
          <div className="w-20 h-20 mx-auto rounded-full border-2 border-[#D4AF37] flex items-center justify-center mb-8">
            <Check size={36} className="text-[#D4AF37]" />
          </div>
          <div className="label-eyebrow mb-4">Application Received</div>
          <h1
            className="font-display text-4xl md:text-5xl text-white mb-6"
            style={{ fontWeight: 300 }}
          >
            Thank you, <span className="italic text-[#D4AF37]">{form.full_name.split(" ")[0]}</span>.
          </h1>
          <p className="text-white/65 leading-relaxed mb-8">
            We've received your application and our team will be in touch within 24 to 48 hours.
            Keep an eye on your email at <span className="text-white">{form.email}</span>.
          </p>
          <div className="border border-white/10 bg-[#0a0a0a] p-5 mb-8 text-sm text-white/60">
            Reference: <span className="text-[#D4AF37] font-mono">{applicationId}</span>
          </div>
          <a
            href="/"
            data-testid="apply-success-home"
            className="inline-flex items-center gap-2 border border-white/20 text-white px-8 py-3 text-sm hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
          >
            Back to home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="apply-page" className="bg-[#050505] min-h-screen">
      {/* Header */}
      <section className="py-16 md:py-24 border-b border-white/5">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="label-eyebrow mb-6">Application</div>
          <h1
            className="font-display text-white leading-[1.05]"
            style={{ fontSize: "clamp(2rem, 5vw, 4rem)", fontWeight: 300 }}
          >
            A few quick details, <br />
            <span className="italic text-[#D4AF37]">and you're on your way.</span>
          </h1>
        </div>
      </section>

      {/* Form */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {/* Stepper */}
          <div className="flex items-center justify-between mb-12">
            {STEPS.map((s, i) => (
              <div key={s.id} className="flex items-center flex-1">
                <div
                  data-testid={`step-indicator-${s.id}`}
                  className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-medium border transition-all ${
                    step >= s.id
                      ? "bg-[#D4AF37] text-black border-[#D4AF37]"
                      : "border-white/20 text-white/40"
                  }`}
                >
                  {step > s.id ? <Check size={14} /> : s.id}
                </div>
                <div className="ml-3 hidden sm:block">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">
                    Step {s.id}
                  </div>
                  <div className={`text-sm ${step >= s.id ? "text-white" : "text-white/40"}`}>
                    {s.label}
                  </div>
                </div>
                {i < STEPS.length - 1 && (
                  <div
                    className={`flex-1 h-px ml-4 ${
                      step > s.id ? "bg-[#D4AF37]" : "bg-white/10"
                    }`}
                  />
                )}
              </div>
            ))}
          </div>

          <div className="bg-[#0a0a0a] border border-white/10 p-8 md:p-10">
            {step === 1 && (
              <div data-testid="step-1-content" className="space-y-5 fade-up">
                <SectionLabel>Personal Details</SectionLabel>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Full Name *">
                    <input data-testid="apply-full-name" type="text" value={form.full_name} onChange={update("full_name")} className={inputCls} />
                  </Field>
                  <Field label="Email *">
                    <input data-testid="apply-email" type="email" value={form.email} onChange={update("email")} className={inputCls} />
                  </Field>
                  <Field label="Phone *">
                    <input data-testid="apply-phone" type="tel" value={form.phone} onChange={update("phone")} className={inputCls} />
                  </Field>
                  <Field label="City *">
                    <select data-testid="apply-city" value={form.city} onChange={update("city")} className={inputCls}>
                      <option value="">Select your city</option>
                      {CITIES.map((c) => <option key={c}>{c}</option>)}
                    </select>
                  </Field>
                  <Field label="Date of Birth *">
                    <input
                      data-testid="apply-dob"
                      type="text"
                      inputMode="numeric"
                      placeholder="DD/MM/YYYY"
                      value={form.date_of_birth}
                      onChange={update("date_of_birth")}
                      className={inputCls}
                    />
                  </Field>
                </div>
              </div>
            )}

            {step === 2 && (
              <div data-testid="step-2-content" className="space-y-5 fade-up">
                <SectionLabel>Business & Licence</SectionLabel>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="ABN (11 digits) *">
                    <input data-testid="apply-abn" type="text" maxLength="14" value={form.abn} onChange={update("abn")} className={inputCls} placeholder="12 345 678 901" />
                  </Field>
                  <Field label="Driver's Licence Number *">
                    <input data-testid="apply-licence" type="text" value={form.drivers_license} onChange={update("drivers_license")} className={inputCls} />
                  </Field>
                  <Field label="Employment Status">
                    <select data-testid="apply-employment" value={form.employment_status} onChange={update("employment_status")} className={inputCls}>
                      <option>Self-employed</option>
                      <option>Sole Trader</option>
                      <option>Company Director</option>
                      <option>Contractor</option>
                      <option>Partnership</option>
                    </select>
                  </Field>
                  <Field label="Average Monthly Income (AUD) *">
                    <input data-testid="apply-income" type="text" value={form.monthly_income} onChange={update("monthly_income")} className={inputCls} placeholder="$8,000" />
                  </Field>
                </div>
                <div className="text-xs text-white/40 pt-2 border-t border-white/5">
                  We use this to match you with a suitable repayment schedule. No credit checks are run.
                </div>
              </div>
            )}

            {step === 3 && (
              <div data-testid="step-3-content" className="space-y-5 fade-up">
                <SectionLabel>Vehicle Preference</SectionLabel>
                <Field label="What vehicle are you interested in? *">
                  <input
                    data-testid="apply-vehicle"
                    type="text"
                    value={form.vehicle_interest}
                    onChange={update("vehicle_interest")}
                    className={inputCls}
                    placeholder="e.g. Toyota HiLux SR5 or 'SUV, family-friendly'"
                  />
                </Field>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Deposit Amount (min $3,000) *">
                    <input
                      data-testid="apply-deposit"
                      type="number"
                      min="4000"
                      step="1"
                      value={form.deposit_amount}
                      onChange={update("deposit_amount")}
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Preferred Term (Years) *">
                    <div className="flex gap-2">
                      {["1", "2", "3"].map((y) => (
                        <button
                          key={y}
                          type="button"
                          data-testid={`apply-term-${y}`}
                          onClick={() => setForm({ ...form, term_years: y })}
                          className={`flex-1 py-3 text-sm border transition-all ${
                            form.term_years === y
                              ? "bg-[#D4AF37] border-[#D4AF37] text-black"
                              : "border-white/15 text-white/70 hover:border-[#D4AF37]/60"
                          }`}
                        >
                          {y} {y === "1" ? "Year" : "Years"}
                        </button>
                      ))}
                    </div>
                  </Field>
                </div>
                <Field label="Additional Notes">
                  <textarea
                    data-testid="apply-notes"
                    rows="4"
                    value={form.additional_notes}
                    onChange={update("additional_notes")}
                    className={`${inputCls} resize-none`}
                    placeholder="Anything else we should know?"
                  />
                </Field>
              </div>
            )}

            {step === 4 && (
              <div data-testid="step-4-content" className="space-y-5 fade-up">
                <SectionLabel>Review & Submit</SectionLabel>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10">
                  {Object.entries({
                    "Full Name": form.full_name,
                    "Email": form.email,
                    "Phone": form.phone,
                    "City": form.city,
                    "DOB": form.date_of_birth,
                    "ABN": form.abn,
                    "Licence": form.drivers_license,
                    "Employment": form.employment_status,
                    "Monthly Income": form.monthly_income,
                    "Vehicle Interest": form.vehicle_interest,
                    "Deposit": `$${form.deposit_amount}`,
                    "Term": `${form.term_years} years`,
                  }).map(([k, v]) => (
                    <div key={k} className="bg-[#0a0a0a] px-5 py-4">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">{k}</div>
                      <div className="text-sm text-white">{v || "—"}</div>
                    </div>
                  ))}
                </div>
                <div className="text-xs text-white/50 leading-relaxed pt-4 border-t border-white/5">
                  By submitting, you confirm the information provided is accurate. Keys to Own
                  will contact you to confirm vehicle availability and finalise the Operating Lease
                  Agreement. Vehicle for business use only.
                </div>
              </div>
            )}

            {/* Nav buttons */}
            <div className="flex justify-between mt-10 pt-6 border-t border-white/10">
              <button
                type="button"
                onClick={back}
                disabled={step === 1}
                data-testid="apply-back"
                className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ArrowLeft size={14} /> Back
              </button>

              {step < 4 ? (
                <button
                  type="button"
                  onClick={next}
                  data-testid="apply-next"
                  className="inline-flex items-center gap-2 bg-[#D4AF37] text-black px-8 py-3 text-sm font-medium hover:bg-[#F6ECCC] transition-colors"
                >
                  Continue <ArrowRight size={14} />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={submit}
                  disabled={submitting}
                  data-testid="apply-submit"
                  className="inline-flex items-center gap-2 bg-[#D4AF37] text-black px-8 py-3 text-sm font-medium hover:bg-[#F6ECCC] transition-colors disabled:opacity-60"
                >
                  {submitting ? "Submitting..." : "Submit Application"}
                  {!submitting && <ArrowRight size={14} />}
                </button>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

const inputCls =
  "w-full bg-[#050505] border border-white/10 text-white px-4 py-3 text-sm focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] focus:outline-none transition-colors";

function SectionLabel({ children }) {
  return (
    <div className="flex items-center gap-3 mb-2">
      <div className="h-px w-8 bg-[#D4AF37]" />
      <span className="label-eyebrow">{children}</span>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.18em] text-white/50 mb-2 block">
        {label}
      </span>
      {children}
    </label>
  );
}
